import { ethers } from 'ethers';
import logger from './logger';

interface SmartDefaultContext {
    selectedContract?: {
        address: string;
        name: string;
        abi: any[];
    };
    functionName?: string;
    chainId?: string;
    userAddress?: string;
    isMultiSig?: boolean;
    isTimelock?: boolean;
}

/**
 * Get smart default values for function parameters based on context
 */
export const getSmartDefault = (
    paramType: string,
    paramName: string,
    context: SmartDefaultContext
): string => {
    const lowerParamName = paramName.toLowerCase();
    const lowerParamType = paramType.toLowerCase();

    // Address type defaults
    if (lowerParamType.includes('address')) {
        return getAddressDefault(lowerParamName, context);
    }

    // Uint256 type defaults
    if (lowerParamType.includes('uint256') || lowerParamType === 'uint') {
        return getUint256Default(lowerParamName, context);
    }

    // Bytes type defaults
    if (lowerParamType.includes('bytes')) {
        return getBytesDefault(lowerParamName, context);
    }

    // Bool type defaults
    if (lowerParamType === 'bool') {
        return getBoolDefault(lowerParamName, context);
    }

    // String type defaults
    if (lowerParamType === 'string') {
        return getStringDefault(lowerParamName, context);
    }

    // Array type defaults
    if (lowerParamType.includes('[]')) {
        return getArrayDefault(lowerParamName, lowerParamType, context);
    }

    // Default fallback
    return getTypeDefault(paramType);
};

/**
 * Get default values for address parameters
 */
const getAddressDefault = (paramName: string, context: SmartDefaultContext): string => {
    // Enhanced address parameter patterns for your specific use cases
    const addressDefaults: { [key: string]: string } = {
        // MultiSig execTransaction/executeTransactionWithChecks parameters
        'to': getMultiSigToDefault(context),
        'target': getTimelockTargetDefault(context),
        'gastoken': ethers.constants.AddressZero,
        'refundreceiver': getRefundReceiverDefault(context),

        // Token parameters
        'recipient': context.userAddress || ethers.constants.AddressZero,
        'spender': context.selectedContract?.address || ethers.constants.AddressZero,
        'owner': context.userAddress || ethers.constants.AddressZero,

        // Timelock parameters
        'executor': context.selectedContract?.address || ethers.constants.AddressZero,
        'proposer': context.userAddress || ethers.constants.AddressZero,

        // Generic fallbacks
        'from': context.userAddress || ethers.constants.AddressZero,
        'sender': context.userAddress || ethers.constants.AddressZero,
        'account': context.userAddress || ethers.constants.AddressZero,
        'user': context.userAddress || ethers.constants.AddressZero,
    };

    // Check for exact matches first
    if (addressDefaults[paramName]) {
        return addressDefaults[paramName];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(addressDefaults)) {
        if (paramName.includes(key)) {
            return value;
        }
    }

    // Default to zero address
    return ethers.constants.AddressZero;
};

/**
 * Get smart default for MultiSig 'to' parameter
 * For execTransaction, 'to' should typically be the TimelockController
 */
const getMultiSigToDefault = (context: SmartDefaultContext): string => {
    // If we're in a MultiSig function, the 'to' address is likely the TimelockController
    if (context.isMultiSig && (context.functionName === 'execTransaction' || context.functionName === 'executeTransactionWithChecks')) {
        // You can add logic here to automatically detect TimelockController address
        // For now, return zero address as placeholder
        return ethers.constants.AddressZero;
    }
    return context.selectedContract?.address || ethers.constants.AddressZero;
};

/**
 * Get smart default for Timelock 'target' parameter
 * For schedule/execute, 'target' should typically be the contract being called (like MemerUSDTierManager)
 */
const getTimelockTargetDefault = (context: SmartDefaultContext): string => {
    // If we're in a Timelock function, the 'target' address is likely a Memer ecosystem contract
    if (context.isTimelock && (context.functionName === 'schedule' || context.functionName === 'execute')) {
        // You can add logic here to suggest common target contracts
        // For now, return zero address as placeholder
        return ethers.constants.AddressZero;
    }
    return context.selectedContract?.address || ethers.constants.AddressZero;
};

/**
 * Get smart default for refundReceiver parameter
 * Should typically default to the selected contract address
 */
const getRefundReceiverDefault = (context: SmartDefaultContext): string => {
    // For MultiSig transactions, refundReceiver should typically be the MultiSig itself
    if (context.isMultiSig) {
        return context.selectedContract?.address || ethers.constants.AddressZero;
    }
    return context.userAddress || context.selectedContract?.address || ethers.constants.AddressZero;
};

/**
 * Get default values for uint256 parameters
 */
const getUint256Default = (paramName: string, context: SmartDefaultContext): string => {
    const uint256Defaults: { [key: string]: string } = {
        // MultiSig parameters
        'value': '0',
        'operation': '0', // CALL operation
        'safetxgas': '0',
        'basegas': '0',
        'gasprice': '0',
        'nonce': '0',

        // Timelock parameters
        'delay': '0', // Will be populated by DelayHelper

        // Token parameters
        'amount': '0',
        'allowance': '0',

        // Generic numeric defaults
        'threshold': '1',
        'count': '1',
        'index': '0',
        'id': '0',
        'timestamp': Math.floor(Date.now() / 1000).toString(),
    };

    // Check for exact matches first
    if (uint256Defaults[paramName]) {
        return uint256Defaults[paramName];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(uint256Defaults)) {
        if (paramName.includes(key)) {
            return value;
        }
    }

    // Special handling for gas-related parameters
    if (paramName.includes('gas')) {
        return '0';
    }

    // Special handling for time-related parameters
    if (paramName.includes('time') || paramName.includes('deadline')) {
        return (Math.floor(Date.now() / 1000) + 3600).toString(); // 1 hour from now
    }

    // Default to zero
    return '0';
};

/**
 * Get default values for bytes parameters
 */
const getBytesDefault = (paramName: string, context: SmartDefaultContext): string => {
    const bytesDefaults: { [key: string]: string } = {
        // MultiSig parameters
        'signatures': '0x',
        'data': '0x',
        'calldata': '0x',

        // Timelock parameters
        'predecessor': ethers.constants.HashZero,
        'salt': '0x0000000000000000000000000000000000000000000000000000000000000000', // Will be populated by SaltGenerator

        // Generic bytes defaults
        'payload': '0x',
        'proof': '0x',
        'signature': '0x',
    };

    // Check for exact matches first
    if (bytesDefaults[paramName]) {
        return bytesDefaults[paramName];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(bytesDefaults)) {
        if (paramName.includes(key)) {
            return value;
        }
    }

    // Special handling for bytes32 parameters
    if (paramName.includes('hash') || paramName.includes('id')) {
        return ethers.constants.HashZero;
    }

    // Default to empty bytes
    return '0x';
};

/**
 * Get default values for boolean parameters
 */
const getBoolDefault = (paramName: string, context: SmartDefaultContext): string => {
    const boolDefaults: { [key: string]: string } = {
        'enabled': 'true',
        'active': 'true',
        'approved': 'false',
        'paused': 'false',
        'emergency': 'false',
    };

    // Check for exact matches first
    if (boolDefaults[paramName]) {
        return boolDefaults[paramName];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(boolDefaults)) {
        if (paramName.includes(key)) {
            return value;
        }
    }

    // Default to false
    return 'false';
};

/**
 * Get default values for string parameters
 */
const getStringDefault = (paramName: string, context: SmartDefaultContext): string => {
    const stringDefaults: { [key: string]: string } = {
        'name': context.selectedContract?.name || 'Contract',
        'symbol': 'TOKEN',
        'description': 'Generated by Transaction Builder',
        'reason': 'Automated transaction',
        'message': 'Transaction executed via secure interface',
    };

    // Check for exact matches first
    if (stringDefaults[paramName]) {
        return stringDefaults[paramName];
    }

    // Check for partial matches
    for (const [key, value] of Object.entries(stringDefaults)) {
        if (paramName.includes(key)) {
            return value;
        }
    }

    // Default to empty string
    return '';
};

/**
 * Get default values for array parameters
 */
const getArrayDefault = (paramName: string, paramType: string, context: SmartDefaultContext): string => {
    // For array inputs, we typically want to start with empty arrays
    // The UI will handle adding/removing elements

    if (paramType.includes('address[]')) {
        // For address arrays, might want to include the user's address
        if (paramName.includes('owner') && context.userAddress) {
            return `["${context.userAddress}"]`;
        }
        return '[]';
    }

    if (paramType.includes('uint256[]') || paramType.includes('uint[]')) {
        return '[]';
    }

    if (paramType.includes('bytes[]')) {
        return '[]';
    }

    if (paramType.includes('string[]')) {
        return '[]';
    }

    // Default empty array
    return '[]';
};

/**
 * Get basic type defaults
 */
export const getTypeDefault = (paramType: string): string => {
    const lowerType = paramType.toLowerCase();

    if (lowerType.includes('address')) {
        return ethers.constants.AddressZero;
    }

    if (lowerType.includes('uint') || lowerType.includes('int')) {
        return '0';
    }

    if (lowerType.includes('bytes')) {
        if (lowerType === 'bytes32') {
            return ethers.constants.HashZero;
        }
        return '0x';
    }

    if (lowerType === 'bool') {
        return 'false';
    }

    if (lowerType === 'string') {
        return '';
    }

    if (lowerType.includes('[]')) {
        return '[]';
    }

    // Fallback
    return '';
};

/**
 * Get context-aware suggestions for parameter values
 */
export const getParameterSuggestions = (
    paramType: string,
    paramName: string,
    context: SmartDefaultContext
): string[] => {
    const lowerParamName = paramName.toLowerCase();
    const suggestions: string[] = [];

    // Address suggestions
    if (paramType.toLowerCase().includes('address')) {
        if (context.userAddress) {
            suggestions.push(context.userAddress);
        }
        if (context.selectedContract?.address) {
            suggestions.push(context.selectedContract.address);
        }
        suggestions.push(ethers.constants.AddressZero);
    }

    // Uint256 suggestions
    if (paramType.toLowerCase().includes('uint256')) {
        if (lowerParamName.includes('amount') || lowerParamName.includes('value')) {
            suggestions.push('0', '1000000000000000000', '1000000000000000000000'); // 0, 1 ETH, 1000 ETH
        } else {
            suggestions.push('0', '1', '10', '100');
        }
    }

    // Bytes suggestions
    if (paramType.toLowerCase().includes('bytes')) {
        suggestions.push('0x');
        if (paramType === 'bytes32') {
            suggestions.push(ethers.constants.HashZero);
        }
    }

    return suggestions;
};

/**
 * Validate parameter value based on type
 */
export const validateParameterValue = (
    paramType: string,
    paramName: string,
    value: string
): { isValid: boolean; error?: string } => {
    const lowerType = paramType.toLowerCase();

    try {
        // Address validation
        if (lowerType.includes('address')) {
            if (!value || value === ethers.constants.AddressZero) {
                return { isValid: true }; // Zero address is valid
            }
            ethers.utils.getAddress(value); // Will throw if invalid
            return { isValid: true };
        }

        // Uint validation
        if (lowerType.includes('uint')) {
            if (!value || value === '') {
                return { isValid: false, error: 'Value is required' };
            }
            const num = ethers.BigNumber.from(value);
            if (num.lt(0)) {
                return { isValid: false, error: 'Value must be non-negative' };
            }
            return { isValid: true };
        }

        // Bytes validation
        if (lowerType.includes('bytes')) {
            if (!value.startsWith('0x')) {
                return { isValid: false, error: 'Bytes value must start with 0x' };
            }
            if (lowerType === 'bytes32' && value.length !== 66) {
                return { isValid: false, error: 'bytes32 must be exactly 66 characters (including 0x)' };
            }
            return { isValid: true };
        }

        // Bool validation
        if (lowerType === 'bool') {
            if (value !== 'true' && value !== 'false') {
                return { isValid: false, error: 'Boolean value must be "true" or "false"' };
            }
            return { isValid: true };
        }

        // Array validation
        if (lowerType.includes('[]')) {
            try {
                JSON.parse(value);
                return { isValid: true };
            } catch {
                return { isValid: false, error: 'Array must be valid JSON format' };
            }
        }

        return { isValid: true };
    } catch (error) {
        return {
            isValid: false,
            error: error instanceof Error ? error.message : 'Invalid value'
        };
    }
};
